import { useGtm } from '@/plugins/useGtm'
import fetcher from '@/services/fetcher'
import {
  type ContentUpdateEvent,
  RealTimeEvents,
} from '@/services/real-time-events'
import type {
  CmsApiResponse,
  InstantWinDraw,
  InstantWinMainPrize,
  MenuItem,
  Nullable,
  Plan,
  Prize,
  ProductOverview,
} from '@/types'
import {
  createDateTimeComparator,
  isToday,
  mapToDateTime,
} from '@/utils/date-time-utils'
import { Icons, PrizeCategories } from '@/utils/helper-objects'
import { translateCurrency } from '@/utils/i18n-helpers'
import { isPlan } from '@/utils/type-guard-utils'
import { toApiUrl, toCmsUrl } from '@/utils/url-generation-utils'
import { DateTime, Interval } from 'luxon'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'

import { useCampaignsStore } from './campaigns'
import { usePlansStore } from './plans'
import { useWinnersStore } from './winners'

type PrizesStoreState = {
  prizes: Map<string, Prize>
  fetchPromise: Promise<Array<Prize>> | null
  hasLoadedAll: boolean // Track if we've fetched all prizes (JSON + CMS)
  isLoading: boolean // Track if prizes are currently being fetched
  hasError: boolean // Track if there was an error fetching prizes
  lastFetchedAt: Nullable<DateTime> // Track when data was last fetched
}

export type PrizeDetails = {
  faq: Prize['faq']
  description: Prize['description']
}

export const usePrizesStore = defineStore('prizes', {
  state: (): PrizesStoreState => ({
    prizes: new Map<string, Prize>(),
    fetchPromise: null,
    hasLoadedAll: false,
    isLoading: false,
    hasError: false,
    lastFetchedAt: null,
  }),

  actions: {
    /**
     * Load prizes from local JSON files.
     * Returns an array of processed prizes (does not mutate store state).
     */
    loadFromJson(): Array<Prize> {
      const intervalPlusSixMonths = Interval.fromDateTimes(
        DateTime.now().minus({ month: 2 }),
        DateTime.now().plus({ month: 6 })
      )

      // Generate a regex to match all prizes in the past 2 months and within the next 6 months
      const prizesRegex =
        '(' +
        intervalPlusSixMonths
          .splitBy({ months: 1 })
          .map((interval: Interval) => {
            return interval.start?.toFormat('yyyyMM')
          })
          .join('|') +
        ')*\\.json'

      const prizesFromJson = import.meta.glob('../data/prizes/*.json', {
        import: 'default',
        eager: true,
      })

      const results: Array<Prize> = []

      for (const path in prizesFromJson) {
        if (!path.match(prizesRegex)) {
          continue
        }

        const prize = prizesFromJson[path] as Prize

        const translatedPrize: Prize = translateCurrency(
          prize,
          this.$i18n.t('currencySymbol'),
          this.$i18n.t('currencyDecimalSymbol')
        )

        const typedPrize = mapToDateTime<Prize>({ ...translatedPrize })

        if (
          typedPrize.value >= this.BIG_PRIZE_VALUE ||
          isToday(typedPrize.drawn_at)
        ) {
          if (!typedPrize.categories.includes(PrizeCategories.Featured)) {
            typedPrize.categories.push(PrizeCategories.Featured)
          }
        }

        results.push(typedPrize)
      }

      return results
    },
    /**
     * Load prizes from the CMS API.
     * Returns an array of processed prizes (does not mutate store state).
     */
    async loadFromCms(): Promise<Array<Prize>> {
      const { data, error } = await fetcher.get<CmsApiResponse<Array<Prize>>>(
        toCmsUrl('draws')
      )

      if (error.value || !data.value?.data) {
        throw error.value || new Error('Failed to fetch prizes from CMS')
      }

      const now = DateTime.now()
      const twoMonthsAgo = now.minus({ month: 2 })
      const sixMonthsAhead = now.plus({ month: 6 })

      // Filter prizes to match date range (past 2 months + next 6 months)
      const filteredPrizes = data.value.data.filter((prize) => {
        const drawnAt = prize.drawn_at
        return drawnAt >= twoMonthsAgo && drawnAt <= sixMonthsAhead
      })

      const results: Array<Prize> = []

      for (const prize of filteredPrizes) {
        // The fetcher already converts dates to DateTime objects and handles currency translation
        const typedPrize = prize

        // Add featured category if needed
        if (
          (typedPrize.value >= this.BIG_PRIZE_VALUE ||
            isToday(typedPrize.drawn_at)) &&
          (prize.is_visible ?? true)
        ) {
          if (!typedPrize.categories.includes(PrizeCategories.Featured)) {
            typedPrize.categories.push(PrizeCategories.Featured)
          }
        }

        results.push(typedPrize)
      }

      return results
    },
    /**
     * Loads prizes from JSON and CMS in parallel, then merges results.
     * JSON data takes priority over CMS. If CMS fails, JSON data is still displayed.
     *
     * @returns {Promise<Array<Prize>>}
     */
    async fetchPrizes(
      withWinners: boolean = true,
      forceRefresh: boolean = false
    ): Promise<Array<Prize>> {
      // If force refreshing, reset flags to reload everything
      if (forceRefresh) {
        this.hasLoadedAll = false
        this.prizes = new Map()
      }

      // If we've loaded all prizes and not forcing refresh, return cached data
      if (!forceRefresh && this.hasLoadedAll) {
        this.isLoading = false
        return this.prizesArray
      }

      // Return the existing fetch promise to prevent multiple simultaneous calls
      if (this.fetchPromise) {
        return this.fetchPromise
      }

      // Set loading state
      this.isLoading = true
      this.hasError = false

      this.fetchPromise = (async () => {
        try {
          const winnersStore = useWinnersStore()

          // Load JSON and CMS in parallel
          const [jsonResult, cmsResult] = await Promise.allSettled([
            Promise.resolve(this.loadFromJson()),
            this.loadFromCms(),
          ])

          // Process JSON first — it takes priority over CMS
          if (jsonResult.status === 'fulfilled') {
            for (const prize of jsonResult.value) {
              this.prizes.set(prize.code, prize)
            }
          }

          // Merge CMS data — only add entries that don't already exist in JSON
          if (cmsResult.status === 'fulfilled') {
            for (const prize of cmsResult.value) {
              if (!this.prizes.has(prize.code)) {
                this.prizes.set(prize.code, prize)
              }
            }
          } else {
            console.warn(
              'CMS fetch failed, using JSON fallback data',
              cmsResult.reason
            )
          }

          // If both sources failed, show error
          if (
            jsonResult.status === 'rejected' &&
            cmsResult.status === 'rejected'
          ) {
            this.hasError = true
            const toast = useToast()
            toast.error('Unable to load prizes. Please try again later.')
          }

          this.hasLoadedAll = true
          this.lastFetchedAt = DateTime.now()

          // Fetch winners if requested
          if (withWinners) {
            const winnerPromises = []
            for (const prize of this.prizes.values()) {
              winnerPromises.push(
                winnersStore.fetchWinnerByPrizeCode(prize.code)
              )
            }
            await Promise.all(winnerPromises)
          }

          return this.prizesArray
        } catch (error) {
          this.hasError = true
          console.error('Error fetching prizes:', error)
          return this.prizesArray
        } finally {
          this.isLoading = false
          this.fetchPromise = null
        }
      })()

      return this.fetchPromise
    },

    async fetchPrize(
      prizeCode: string,
      forceRefresh: boolean = false
    ): Promise<Prize | null> {
      // Check cache first
      if (!forceRefresh && this.prizes.has(prizeCode)) {
        return this.prizes.get(prizeCode) ?? null
      }

      try {
        let prize: Prize | null = null

        // Try JSON first (priority source) using dynamic import
        try {
          const jsonModule = await import(`../data/prizes/${prizeCode}.json`)

          if (jsonModule.default) {
            const translatedPrize: Prize = translateCurrency(
              jsonModule.default,
              this.$i18n.t('currencySymbol'),
              this.$i18n.t('currencyDecimalSymbol')
            )

            prize = mapToDateTime<Prize>(translatedPrize)
          }
        } catch (jsonError) {
          console.warn(
            `File not found for ${prizeCode}, trying CMS:`,
            jsonError
          )
        }

        // If JSON didn't load, try CMS as fallback
        if (!prize) {
          const { data, error } = await fetcher.get<CmsApiResponse<Prize>>(
            toCmsUrl(`draws/${prizeCode}`)
          )

          if (error.value || !data.value?.data) {
            throw error.value || new Error('Prize not found')
          }

          // The fetcher already handles date conversion and currency translation for CMS
          prize = data.value.data
        }

        if (!prize) {
          throw new Error(`Prize ${prizeCode} could not be loaded`)
        }

        // Add featured category if needed
        if (prize.value >= this.BIG_PRIZE_VALUE || isToday(prize.drawn_at)) {
          if (!prize.categories.includes(PrizeCategories.Featured)) {
            prize.categories.push(PrizeCategories.Featured)
          }
        }

        // Cache the prize
        this.prizes.set(prize.code, prize)

        return prize
      } catch (error: any) {
        console.error('Failed to fetch prize:', prizeCode, error)
        // TODO: Add Datadog custom log

        return null
      }
    },

    /**
     * Returns all prizes within a specified category.
     *
     * @param {string} category
     * @returns {Array<Prize>}
     */
    prizesByCategory(category: string): Array<Prize> {
      return this.prizesArray.filter((prize: Prize) =>
        prize.categories.includes(category)
      )
    },

    /**
     * Checks if a prize is currently being drawn.
     * Compares the current datetime with drawn datetime + 30minutes.
     *
     * @param {Prize} prize
     * @returns {boolean}
     */
    isDrawLive(prize: Prize): boolean {
      const drawPeriod = Interval.fromDateTimes(
        prize.drawn_at.minus({ minute: 5 }),
        prize.drawn_at.plus({ minute: 15 })
      )

      return drawPeriod.contains(DateTime.now())
    },

    generateCategories(
      prizes: Array<Prize | InstantWinMainPrize | InstantWinDraw>,
      allCategoryType: string = PrizeCategories.AllPrizes
    ): Array<MenuItem> {
      const categories = new Map<string, MenuItem>()

      categories.set(PrizeCategories.Featured, {
        icon: Icons.Star,
        text: PrizeCategories.Featured,
        code: PrizeCategories.Featured,
        class: 'text-sm',
      })

      categories.set(allCategoryType, {
        icon: Icons.Calendar,
        text: allCategoryType,
        code: allCategoryType,
        class: 'text-sm',
      })

      for (const prize of prizes) {
        // For InstantWinDraw, categories are in main_prize.categories
        let prizeCategories: string[] = []
        if ('main_prize' in prize && prize.main_prize?.categories) {
          prizeCategories = prize.main_prize.categories
        } else if ('categories' in prize) {
          prizeCategories = prize.categories
        }

        for (const prizeCategory of prizeCategories) {
          if (categories.has(prizeCategory)) {
            continue
          }

          switch (prizeCategory) {
            case PrizeCategories.InstantWins:
              categories.set(PrizeCategories.InstantWins, {
                icon: Icons.Lightening,
                text: 'Instant Prizes',
                code: 'instant-prizes',
                class: 'text-sm',
              })
              break

            case PrizeCategories.MysteryBoxes:
              categories.set(PrizeCategories.MysteryBoxes, {
                icon: Icons.Present,
                text: PrizeCategories.MysteryBoxes,
                code: 'mystery-boxes',
                class: 'text-sm',
              })
              break

            case PrizeCategories.InstantSpins:
              categories.set(PrizeCategories.InstantSpins, {
                icon: Icons.SlotMachine,
                text: PrizeCategories.InstantSpins,
                code: 'instant-spins',
                class: 'text-sm',
              })
              break

            case PrizeCategories.InstantPacks:
              categories.set(PrizeCategories.InstantPacks, {
                icon: Icons.Packs,
                text: PrizeCategories.InstantPacks,
                code: 'instant-packs',
                class: 'text-sm',
              })
              break

            case PrizeCategories.Cash:
              categories.set(PrizeCategories.Cash, {
                icon: Icons.CashNote,
                text: 'Tax Free Cash',
                code: PrizeCategories.Cash,
                class: 'text-sm',
              })
              break

            case PrizeCategories.DreamCars:
              categories.set(PrizeCategories.DreamCars, {
                icon: Icons.Car,
                text: 'Cars & Motor',
                code: PrizeCategories.DreamCars,
                class: 'text-sm',
              })
              break

            case PrizeCategories.Experiences:
              categories.set(PrizeCategories.Experiences, {
                icon: Icons.Experiences,
                text: PrizeCategories.Experiences,
                code: PrizeCategories.Experiences,
                class: 'text-sm',
              })
              break

            case PrizeCategories.GetAways:
              categories.set(PrizeCategories.GetAways, {
                icon: Icons.GetAways,
                text: PrizeCategories.GetAways,
                code: PrizeCategories.GetAways,
                class: 'text-sm',
              })
              break

            case PrizeCategories.Technology:
              categories.set(PrizeCategories.Technology, {
                icon: Icons.Technology,
                text: PrizeCategories.Technology,
                code: PrizeCategories.Technology,
                class: 'text-sm',
              })
              break

            case PrizeCategories.LuxuryItem:
              categories.set(PrizeCategories.LuxuryItem, {
                icon: Icons.Watch,
                text: 'Luxury Items',
                code: PrizeCategories.LuxuryItem,
                class: 'text-sm',
              })
              break

            default:
              categories.set(prizeCategory, {
                icon: prizeCategory,
                text: prizeCategory,
                code: prizeCategory,
                class: 'text-sm',
              })
          }
        }
      }

      return Array.from<MenuItem>(categories.values())
    },

    generateDetails(
      prize: Pick<
        Prize,
        'faq' | 'description' | 'categories' | 'value' | 'title'
      >
    ): PrizeDetails {
      const { n, t } = useI18n()
      const faq = prize.faq
      let description = prize.description

      if (!description && prize.categories.includes(PrizeCategories.Cash)) {
        description = t('page.prizes.generic_details.cash.description', {
          value: n(prize.value, 'currency-short'),
        })
      }

      if (
        !description &&
        prize.categories.includes(PrizeCategories.DreamCars)
      ) {
        description = t('page.prizes.generic_details.car.description', {
          car: prize.title,
        })
      }

      if (
        !description &&
        prize.categories.includes(PrizeCategories.Experiences)
      ) {
        description = t('page.prizes.generic_details.experience.description', {
          experience: prize.title,
        })
      }

      if (!description && prize.categories.includes(PrizeCategories.GetAways)) {
        description = t('page.prizes.generic_details.get_away.description', {
          get_away: prize.title,
        })
      }

      // if (!description && prize.categories.includes(PrizeCategories.Technology)) {
      //   description =
      //     description ??
      //     t('page.prizes.generic_details.technology.description', {
      //       technology: prize.title,
      //     })
      // }

      return {
        faq,
        description,
      }
    },

    async submitPrize(listId: string, formData: any) {
      // Source is automatically detected from UTM params, referrer, etc.
      // No need to hardcode 'ORGANIC' - the fetcher handles intelligent source detection
      if (formData.partnership_campaign_id) {
        const { data, error } = await fetcher.post<{
          success: boolean
          redirectUrl?: string
        }>(
          toApiUrl('/subscribe-lead/create-lead/'),
          {
            ...formData,
          },
          true, // useCredentials
          true // alwaysIncludeCredentials - forces cookies to always be sent
        )

        if (error.value) {
          throw error.value
        }

        const gtm = await useGtm()
        gtm?.trackEvent({
          event: 'lead',
        })

        return data.value
      }

      const { data, error } = await fetcher.post(
        toApiUrl('/subscribe-lead/email-phone/'),
        {
          list_id: listId,
          ...formData,
        },
        true, // useCredentials
        true // alwaysIncludeCredentials - forces cookies to always be sent for lead tracking
      )

      if (error.value) {
        throw error.value
      }

      const gtm = await useGtm()
      gtm?.trackEvent({
        event: 'lead',
      })

      return data.value?.success
    },

    productPlans(product: ProductOverview): Array<Plan> {
      const plansStore = usePlansStore()

      return product.plans
        .map((planType: string) => plansStore.getPlanByTier(planType))
        .filter((p): p is Plan => isPlan(p))
    },
    /**
     * Subscribe to real-time content updates for prizes.
     */
    subscribeToUpdates() {
      RealTimeEvents.subscribe(
        RealTimeEvents.Channels.ContentUpdates,
        RealTimeEvents.Events.PrizePublished,
        true,
        (event: ContentUpdateEvent<Prize>) => {
          console.log('Prize content update received:', event)

          if (event.action === 'deleted') {
            this.prizes.delete(event.code)
            return
          }

          // action === 'updated'
          if (event.data) {
            // Transform broadcast data the same way the fetcher does
            const transformed = mapToDateTime<Prize>(
              translateCurrency(
                event.data,
                this.$i18n.t('currencySymbol'),
                this.$i18n.t('currencyDecimalSymbol')
              )
            )

            // Add featured category if needed
            if (
              (transformed.value >= this.BIG_PRIZE_VALUE ||
                isToday(transformed.drawn_at)) &&
              (transformed.is_visible ?? true)
            ) {
              if (!transformed.categories.includes(PrizeCategories.Featured)) {
                transformed.categories.push(PrizeCategories.Featured)
              }
            }

            this.prizes.set(event.code, transformed)
          } else {
            // Fallback: fetch just this one prize from CMS
            void this.fetchPrize(event.code, true)
          }
        }
      )
    },
    /**
     * Unsubscribe from real-time content updates.
     */
    unsubscribeFromUpdates() {
      RealTimeEvents.unsubscribe(
        RealTimeEvents.Channels.ContentUpdates,
        RealTimeEvents.Events.PrizePublished
      )
    },
  },

  getters: {
    prizesArray({ prizes }): Array<Prize> {
      return Array.from<Prize>(prizes.values())
        .sort(createDateTimeComparator('drawn_at'))
        .filter((prize: Prize) => prize.is_visible ?? true)
    },
    /**
     * Returns prizes (filtered by visibility) sorted with the following priority:
     * 1. Non-shared prizes sorted by close_at ascending
     * 2. The closest upcoming shared prize (by drawn_at), regardless of value
     * 3. Shared prizes with value >= BIG_PRIZE_VALUE sorted by close_at ascending
     * 4. Remaining shared prizes sorted by close_at ascending
     *
     * This ensures that the most relevant shared prize is prominently displayed, while still prioritising non-shared prizes and higher value shared prizes.
     *
     * @returns Function that accepts tense parameter and returns Array<Prize>
     */
    sortedPrizes(): (tense?: 'future' | 'past' | 'all') => Array<Prize> {
      return (tense: 'future' | 'past' | 'all' = 'all'): Array<Prize> => {
        const allPrizes = this.prizesArray
          .filter((prize: Prize) => {
            const isVisible = prize.is_visible ?? true

            if (!isVisible) {
              return false
            }

            if (tense === 'future') {
              return (
                prize.drawn_at.diffNow().milliseconds >= 0 &&
                prize.open_at.diffNow().milliseconds < 0
              )
            } else if (tense === 'past') {
              return prize.drawn_at.diffNow().milliseconds < 0
            }

            return true
          })
          .sort(createDateTimeComparator('close_at'))

        // Get the closest prize by date from all prizes
        const closestPrize = allPrizes.shift()

        // Split remaining prizes by value
        const [nonSharedPrizes, mainPrizes, regularPrizes] = allPrizes.reduce(
          (acc, prize: Prize) => {
            if (closestPrize?.code === prize.code) {
              return acc
            }

            if (!prize.is_shared) {
              acc[0].push(prize)
            } else if (prize.value >= this.BIG_PRIZE_VALUE) {
              acc[1].push(prize)
            } else {
              acc[2].push(prize)
            }
            return acc
          },
          [[], [], []] as [Array<Prize>, Array<Prize>, Array<Prize>]
        )

        // Order: non-shared, then closest, main, regular
        return [
          ...nonSharedPrizes,
          ...(closestPrize ? [closestPrize] : []),
          ...mainPrizes,
          ...regularPrizes,
        ]
      }
    },
    futurePrizes(): Array<Prize> {
      return this.sortedPrizes('future').filter(
        (prize: Prize) =>
          prize.drawn_at.diffNow().milliseconds >= 0 &&
          prize.open_at.diffNow().milliseconds < 0
      )
    },
    pastPrizes(): Array<Prize> {
      return this.sortedPrizes('past')
        .filter((prize: Prize) => prize.drawn_at.diffNow().milliseconds < 0)
        .reverse()
    },
    nextPrize(): null | Prize {
      return (
        this.prizesArray
          .filter(
            (prize: Prize) =>
              prize.drawn_at.diffNow().milliseconds >= 0 &&
              prize.open_at.diffNow().milliseconds < 0
          )
          .sort(createDateTimeComparator('drawn_at'))?.[0] ?? null
      )
    },
    productOverviews(): Array<ProductOverview> {
      const campaignsStore = useCampaignsStore()
      const featuredCampaignImage = campaignsStore.featuredCampaign?.main_image

      return [
        {
          __type: 'Prize',
          code: 'monthly-draws',
          title: '30+ prize draws monthly',
          description: 'Automatically enter to win luxury prizes!',
          image: {
            src:
              featuredCampaignImage?.tablet ??
              '/assets/images/prizes/mercedes-c63/mercedes-c63-1_1.jpg',
            alt: featuredCampaignImage?.alt ?? 'Mercedes-AMG C63',
          },
          plans: [],
        },
        {
          __type: 'Benefit',
          code: 'tasty-savings',
          title: 'Tasty savings are waiting',
          description: '2-for-1 meals and discounts with Tastecard.',
          image: {
            src: '/assets/images/benefits/pizza-express.webp',
            alt: 'Pizza Express Logo',
          },
          plans: [],
        },
        {
          __type: 'Benefit',
          code: 'exclusive-treats',
          title: 'Exclusive treats',
          description:
            'Choose Greggs, Costa, Krispy Kreme, Deliveroo, and more!',
          image: {
            src: '/assets/images/benefits/greggs.webp',
            alt: 'Greggs Baguette',
          },
          plans: ['platinum', 'vip'],
        },
        {
          __type: 'Benefit',
          code: 'vip-perks',
          title: 'VIP cinema perks',
          description: 'Get 2 Vue cinema tickets every month.',
          image: {
            src: '/assets/images/benefits/vue.webp',
            alt: 'Vue Cinema Logo',
          },
          plans: ['vip'],
        },
      ]
    },
  },
})
